import { useShallow } from 'zustand/react/shallow';

import { useSetClientStorageItem } from '@/entities/clientStorage';

import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';

import type { Answers, QuestionId, RoleAnswer, UseCaseAnswer } from '../lib/types';

import {
  RECORDING_SURVEY_COPY,
  RECORDING_SURVEY_STORAGE_KEY,
  ROLE_OPTIONS,
  USE_CASE_OPTIONS,
} from '../lib/constants';
import { isValidOtherText } from '../lib/validationSchema';
import { useRecordingWelcomeSurveyStore } from './useRecordingWelcomeSurveyStore';

type AnyAnswer = RoleAnswer | UseCaseAnswer;

const buildAnswerPayload = (
  questionId: QuestionId,
  answer: AnyAnswer,
  otherText: string,
): { answer: AnyAnswer; answerText?: string; questionId: QuestionId } => {
  const trimmed = otherText.trim();
  return {
    answer,
    questionId,
    ...(answer === 'other' && trimmed.length > 0 ? { answerText: trimmed } : {}),
  };
};

const buildCompletePayload = (answers: Answers) => {
  const { role, roleOtherText, useCase } = answers;
  return {
    ...(role !== undefined && { role }),
    ...(useCase !== undefined && { useCase }),
    ...(role === 'other' && roleOtherText && roleOtherText.trim().length > 0
      ? { roleOtherText: roleOtherText.trim() }
      : {}),
  };
};

export const useRecordingWelcomeSurveyForm = () => {
  const { answers, close, currentQuestion, goToRole, goToUseCase, setRoleAnswer, setUseCaseAnswer } =
    useRecordingWelcomeSurveyStore(
      useShallow((s) => ({
        answers: s.answers,
        close: s.close,
        currentQuestion: s.currentQuestion,
        goToRole: s.goToRole,
        goToUseCase: s.goToUseCase,
        setRoleAnswer: s.setRoleAnswer,
        setUseCaseAnswer: s.setUseCaseAnswer,
      })),
    );

  const { mutate: setItem } = useSetClientStorageItem();

  const markSurveyCompleted = () => {
    setItem({ key: RECORDING_SURVEY_STORAGE_KEY, value: 'true' });
  };

  const isRoleQuestion = currentQuestion === 'role';
  const selected = isRoleQuestion ? answers.role : answers.useCase;
  const otherText = answers.roleOtherText ?? '';
  const isOtherSelected = isRoleQuestion && selected === 'other';

  const isContinueDisabled =
    selected === undefined || (isOtherSelected && !isValidOtherText(otherText));

  const onSelect = (id: AnyAnswer) => {
    if (isRoleQuestion) {
      setRoleAnswer(id as RoleAnswer, answers.roleOtherText);
    } else {
      setUseCaseAnswer(id as UseCaseAnswer);
    }
  };

  const onOtherTextChange = (value: string) => {
    if (!isRoleQuestion) return;
    setRoleAnswer('other', value);
  };

  const onContinue = () => {
    if (selected === undefined || isContinueDisabled) return;

    MixpanelEvents.recordingWelcomeSurveyQuestionAnswered(
      buildAnswerPayload(currentQuestion, selected, otherText),
    );

    if (isRoleQuestion) {
      goToUseCase();
      return;
    }

    MixpanelEvents.recordingWelcomeSurveyComplete(buildCompletePayload(answers));
    markSurveyCompleted();
    close();
  };

  const onBack = () => {
    goToRole();
  };

  const onDismiss = () => {
    MixpanelEvents.recordingWelcomeSurveyDismiss({ atQuestionId: currentQuestion });
    markSurveyCompleted();
    close();
  };

  return {
    title: RECORDING_SURVEY_COPY[currentQuestion].title,
    canGoBack: !isRoleQuestion,
    currentQuestion,
    isContinueDisabled,
    isOtherSelected,
    options: isRoleQuestion ? ROLE_OPTIONS : USE_CASE_OPTIONS,
    otherText,
    selected,
    subtitle: RECORDING_SURVEY_COPY[currentQuestion].subtitle,
    onBack,
    onContinue,
    onDismiss,
    onOtherTextChange,
    onSelect,
  };
};
